// A metric with no data to compute it from renders as an em dash, never as 0%
// or "null%". Null is a real value in the analytics contract: "nothing closed
// in this window" and "no baseline to grow from" are not zero.
export const NO_DATA = '—';

export function percentValue(rate: number | null | undefined, fractionDigits?: number): string {
  if (rate === null || rate === undefined) return NO_DATA;
  return `${fractionDigits === undefined ? rate : rate.toFixed(fractionDigits)}%`;
}

// `d`, `h` and `m` are notation, not copy: Spanish and Portuguese use the same
// abbreviations, so translating them buys nothing and a compound like "2h 35m"
// would need a key per shape. Deliberately left untranslated — the spelled-out
// unit lives in `useMetricDisplay.daysValue`, which is where a word belongs.
export function durationMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return NO_DATA;
  // Rounded to whole minutes BEFORE the split. Flooring the hours first and
  // rounding the remainder after produced counts that cannot exist: 1439.9856
  // minutes rendered "23h 60m", and 59.6 rendered "60m". Reachable since
  // `daysValue` began routing sub-day durations through here.
  const total = Math.round(minutes);
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function durationDays(days: number | null | undefined): string {
  return days === null || days === undefined ? NO_DATA : `${days.toFixed(1)}d`;
}

// A label that names nothing — no bottleneck, no best stage — is "—" too.
export function labelValue(label: string | null | undefined): string {
  return label === null || label === undefined || label === '' ? NO_DATA : label;
}

// A score the API could not compute is "—", never "0". Zero is a real pipeline
// reading — an empty pipeline — and a funnel with no open stage has not made
// that measurement.
export function scoreValue(score: string | null | undefined): string {
  return score === null || score === undefined ? NO_DATA : score;
}

export interface TrendBadge {
  value: number;
  direction: 'up' | 'down';
}

export function trendBadge(growth: number | null | undefined): TrendBadge | undefined {
  if (growth === null || growth === undefined) return undefined;
  return { value: Math.abs(growth), direction: growth >= 0 ? 'up' : 'down' };
}

// A count the API could not establish is "—", never 0. On an alerting card a
// confident zero reads as "nobody is being neglected" when the truth may be
// "nothing here is old enough to judge".
export function countValue(count: number | null | undefined): string {
  return count === null || count === undefined ? NO_DATA : `${count}`;
}
